import { useIntl } from '@cookbook/solid-intl';
import {
  arrayToObjectByKey,
  deepExtendObjects,
  flattenObject,
  pluralize
} from '~/utils/commonUtils.ts';
import defaultTranslations from './translations/default.json';
import { timeSignatures } from '~/consts.ts';
import logger from '~/utils/logger';

export enum SUPPORTED_LOCALES {
  enUS = 'en-US',
  esES = 'es-ES'
}

export enum HourFormat {
  TWELVE_HOUR = '12h',
  TWENTY_FOUR_HOUR = '24h'
}

export const DEFAULT_LOCALE: string = SUPPORTED_LOCALES.enUS;

const flattenedDefaultTranslations = flattenObject(defaultTranslations);

async function loadTranslations(locale: string) {
  const supportedLocaleList: Array<string> = Object.values(SUPPORTED_LOCALES);
  if (!supportedLocaleList.includes(locale)) {
    logger.error(
      `Invalid locale : ${locale}. Falling back to ${DEFAULT_LOCALE}`
    );
    return flattenedDefaultTranslations;
  }
  try {
    let translations = (
      await import(`./translations/${locale.toLowerCase()}.json`)
    ).default;
    translations = deepExtendObjects(defaultTranslations, translations);
    return flattenObject(translations);
  } catch (e) {
    logger.error(
      `Failed to fetch translations for locale ${locale}. Falling back to ${DEFAULT_LOCALE}`
    );
    return flattenedDefaultTranslations;
  }
}

function getLocalizedString(
  translationKey: string,
  messageVars?: Record<string, any>,
  defaultMessage?: string
): string {
  // intl (i.e. useIntl()) should be passed as a key messageVars
  // for out of the context execution
  // e.g. in case of api response handlers, event handlers etc
  const { intl, ...vars } = messageVars || {};
  const newIntl = intl || useIntl();
  // we should allow empty string as default message
  const defaultMessageStr =
    defaultMessage === undefined || defaultMessage === null
      ? flattenedDefaultTranslations[translationKey] || translationKey
      : defaultMessage;
  return newIntl.formatMessage(
    {
      id: translationKey,
      // defaultMessage is already taken care of in loadTranslations in utils.ts
      // here it is for redundancy
      defaultMessage: defaultMessageStr
    },
    vars
  );
}

type RelativeTimeUnit =
  | 'second'
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year';

function getDiffByUnit(diffInMs: number): {
  diff: number | null;
  unit: RelativeTimeUnit | null;
} {
  const isNegative = diffInMs < 0;
  const positiveDiffInMs = Math.abs(diffInMs);
  let diff;
  let unit;
  if (positiveDiffInMs < timeSignatures.MIN_IN_MILLIS) {
    diff = Math.floor(positiveDiffInMs / timeSignatures.SEC_IN_MILLIS);
    unit = 'second';
  } else if (positiveDiffInMs < timeSignatures.HOUR_IN_MILLIS) {
    diff = Math.floor(positiveDiffInMs / timeSignatures.MIN_IN_MILLIS);
    unit = 'minute';
  } else if (positiveDiffInMs < timeSignatures.DAY_IN_MILLIS) {
    diff = Math.floor(positiveDiffInMs / timeSignatures.HOUR_IN_MILLIS);
    unit = 'hour';
  } else if (positiveDiffInMs < timeSignatures.MONTH_IN_MILLIS) {
    diff = Math.floor(positiveDiffInMs / timeSignatures.DAY_IN_MILLIS);
    unit = 'day';
  } else if (positiveDiffInMs < timeSignatures.YEAR_IN_MILLIS) {
    diff = Math.floor(positiveDiffInMs / timeSignatures.MONTH_IN_MILLIS);
    unit = 'month';
  } else if (positiveDiffInMs < 100 * timeSignatures.YEAR_IN_MILLIS) {
    // Show years till 100 years
    diff = Math.floor(positiveDiffInMs / timeSignatures.YEAR_IN_MILLIS);
    unit = 'year';
  } else {
    // case of never where diff in mins is more than 100 years
    return {
      diff: null,
      unit: null
    };
  }

  return {
    diff: isNegative ? -1 * diff : diff,
    unit: unit as RelativeTimeUnit
  };
}

function getRelativeTime(time: string | Date, now: Date = new Date()): string {
  const newIntl = useIntl();

  if (time === '') {
    logger.error(
      'Failed to format relative time, param timeStr is empty string'
    );
    return '';
  }

  const dateObj = time instanceof Date ? time : new Date(time);
  const msDelta = dateObj.getTime() - now.getTime();
  const { diff, unit } = getDiffByUnit(msDelta);

  if (diff === null) {
    return `- ${getLocalizedString('commons.relative_time_never')}`;
  }

  // Past: keep existing behavior (“2 days ago”, “yesterday”, etc.)
  if (diff <= 0) {
    return newIntl.formatRelativeTime(diff, unit, {
      numeric: 'auto',
      style: 'long'
    });
  }

  // Future: “17 days remaining”
  const n = Math.abs(diff);
  return `${n} ${n > 1 ? pluralize(String(unit)) : unit} ${getLocalizedString('commons.relative_time_remaining') || 'remaining'}`;
}

function getFormattedDate(
  time: string | Date,
  {
    includeTime = false,
    includeSeconds = false,
    upperCase = false,
    is24HourFormat = false,
    timeZone
  }: {
    includeTime?: boolean;
    includeSeconds?: boolean;
    upperCase?: boolean;
    is24HourFormat?: boolean;
    timeZone?: string;
  } = {}
): string {
  const newIntl = useIntl();
  if (time === '') {
    logger.error('Failed to format date, param timeStr is empty string');
    return '';
  }
  const dateObj = time instanceof Date ? time : new Date(time);

  const dateParts = arrayToObjectByKey(
    newIntl.formatTimeToParts(dateObj, {
      year: 'numeric',
      day: '2-digit',
      month: 'short',
      formatMatcher: 'basic',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: !is24HourFormat,
      dayPeriod: 'long',
      ...(timeZone ? { timeZone } : {})
    }),
    'type'
  );

  // @todo revisit the date format
  const dateStr = `${dateParts.day.value}-${dateParts.month.value}-${dateParts.year.value}`;

  if (!includeTime) {
    return dateStr;
  }

  const timeStr = `${dateParts.hour.value}:${dateParts.minute.value}${includeSeconds ? `:${dateParts.second.value}` : ''}`;

  if (is24HourFormat) return `${dateStr} ${timeStr}`;

  const period = dateParts.dayPeriod?.value || '';
  const formattedPeriod = upperCase
    ? period.toUpperCase()
    : period.toLowerCase();

  return `${dateStr} ${timeStr}${includeSeconds ? ` ${formattedPeriod}` : formattedPeriod}`;
}

function getUTCOffsetString(): string {
  const offsetInMinutes = -new Date().getTimezoneOffset();
  if (offsetInMinutes === 0) return 'UTC';

  const sign = offsetInMinutes >= 0 ? '+' : '-';
  const absMinutes = Math.abs(offsetInMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;

  return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
}

export {
  loadTranslations,
  getLocalizedString,
  getRelativeTime,
  getFormattedDate,
  getUTCOffsetString
};
